import type { PermissionFlag, Rule } from '../lib/types.js';
import './RuleTable.css';

export const FLAG_LABELS: { key: PermissionFlag; short: string; scrittura?: boolean }[] = [
  { key: 'list', short: 'vedere' },
  { key: 'download', short: 'scaricare' },
  { key: 'upload', short: 'caricare', scrittura: true },
  { key: 'delete', short: 'cancellare', scrittura: true },
  { key: 'createFolder', short: 'creare cartelle', scrittura: true },
  { key: 'rename', short: 'rinominare', scrittura: true },
];

const SCOPE_LABELS: Record<Rule['scope'], string> = {
  template: 'template',
  client: 'organizzazione',
  user: 'utenza',
};

export function FlagList({ perms }: { perms: Record<PermissionFlag, boolean> }) {
  const attivi = FLAG_LABELS.filter((f) => perms[f.key]);
  if (attivi.length === 0) return <span className="rtab__deny">nessun permesso</span>;
  return (
    <span className="rtab__flags">
      {attivi.map((f) => (
        <span key={f.key} className={f.scrittura ? 'is-write' : undefined}>
          {f.short}
        </span>
      ))}
    </span>
  );
}

/**
 * Elenco di regole nell'ordine in cui le valuta il motore.
 *
 * L'ordine non è cosmetico: la prima riga è quella che vince sui path che copre
 * (§6.4), e mostrarle in ordine di inserimento farebbe credere il contrario.
 * Qui si ordina per specificità/esattezza/priorità/id — lo `scope` non entra
 * perché una tabella sola contiene sempre regole dello stesso livello; quando
 * si mostrano insieme regole di livelli diversi si usa `showScope`, e la
 * colonna dice a quale livello appartiene ciascuna.
 */
export function RuleTable({
  rules,
  onEdit,
  onDelete,
  showScope = false,
}: {
  rules: Rule[];
  onEdit?: (rule: Rule) => void;
  onDelete?: (rule: Rule) => void;
  showScope?: boolean;
}) {
  const azioni = onEdit !== undefined || onDelete !== undefined;

  return (
    <div className="rtab__scroll">
      <table className="rtab">
        <thead>
          <tr>
            <th>Percorso</th>
            {showScope ? <th>Livello</th> : null}
            <th>Su</th>
            <th>Permessi</th>
            <th className="rtab__num" title="Più alta = vince">
              Spec.
            </th>
            {azioni ? (
              <th>
                <span className="visually-hidden">Azioni</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {[...rules]
            .sort(
              (a, b) =>
                (b.specificity ?? 0) - (a.specificity ?? 0) ||
                (b.exactness ?? 0) - (a.exactness ?? 0) ||
                b.priority - a.priority ||
                b.id - a.id,
            )
            .map((r) => (
              <tr key={r.id} className={r.patternValido ? undefined : 'is-broken'}>
                <td>
                  <code>{r.pathPattern}</code>
                  {r.patternValido ? null : (
                    <span className="rtab__broken">pattern non valido: ignorata</span>
                  )}
                  {r.note ? <span className="rtab__note">{r.note}</span> : null}
                </td>
                {showScope ? <td className="rtab__muted">{SCOPE_LABELS[r.scope]}</td> : null}
                <td className="rtab__muted">
                  {r.appliesTo === 'any' ? 'tutto' : r.appliesTo === 'folder' ? 'cartelle' : 'file'}
                </td>
                <td>
                  <FlagList perms={r.permissions} />
                </td>
                <td className="rtab__num rtab__muted">
                  {r.specificity ?? '—'}
                  {r.exactness === 1 ? '·e' : ''}
                </td>
                {azioni ? (
                  <td className="rtab__actions">
                    {onEdit ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onEdit(r)}
                      >
                        Modifica
                      </button>
                    ) : null}
                    {onDelete ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onDelete(r)}
                      >
                        Elimina
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
